import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex'

import * as dateutil from '@appbase/util/date-helper'
import * as types from '@webparts/store/types'
import { CalendarEventResponseData, CalendarFolderInfo, CalendarDayInfo, CalendarEvent } from './types'

import CalendarDay from './CalendarDay.vue'

@Component({
    components: { 
        CalendarDay
    },
    computed: mapGetters({
        monthlyEvents: types.WEBPART_CALENDARMONTHLYEVENT_DATA,
        folders: types.WEBPART_CALENDARFOLDER_DATA
    }),
    methods: {
        ...mapActions({
            getMonthlyEvents: types.WEBPART_CALENDARMONTHLYEVENT_DATA
        })
    }
})
export default class Calendar extends Vue {
    getMonthlyEvents!: (payload: { start: Date, end: Date }) => Promise<graph.CalendarItem[]>

    monthlyEvents!: graph.CalendarItem[];
    folders!: CalendarFolderInfo[];

    monthNames: Array<string> = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ]   

    //
    // ────────────────────────────────────────────────── I ──────────
    //   :::::: P R O P S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────
    //

    @Prop({ type: String }) value!: string
    @Prop({ type: Array }) weeks!: Array<string>
    @Prop({ type: Boolean, default: false }) preview!: boolean

    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: D A T A : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //

    MonthMatrix: Array<Array<CalendarDayInfo>> = []
    selectedEvent: Array<CalendarEvent> = []

    matrixStart: Date = new Date()
    matrixEnd: Date = new Date()

    //
    // ──────────────────────────────────────────────────────── I ──────────
    //   :::::: C O M P U T E D : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────────────
    //

    get date(): Date {
        return dateutil.IEDate(this.value)
    }

    get YearMonthDateFormat() {
        const monthName = this.monthNames[this.date.getMonth() - 1]
        return dateutil.uGetDateFormat(`${monthName} yyyy`, this.date)
    }

    createHeaderTitle() {
        const year = this.date.getFullYear()
        const month = this.date.getMonth() + 1

        return `${year}.${month}`
    }

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: W A T C H E S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //

    @Watch('value')
    onValueChanged(newVal: string, oldVal: string) {
        const oldDate = dateutil.IEDate(oldVal)
        const newDate = dateutil.IEDate(newVal)
        const newMonth = newDate.getMonth()
        const newDay = newDate.getDate()
        if (oldDate.getMonth() !== newMonth) {
            this.calcMatrix(true)
        } else {
            for (var row of this.MonthMatrix) {
                for (var day of row) {
                    if (day.selected) day.selected = false
                    if (day.month === newMonth && day.day === newDay)
                        day.selected = true
                }
            }
        }
    }

    @Watch('monthlyEvents')
    onEventsChanged(newVal: graph.CalendarItem[]) {
        const colorEvent = {
            category: '',
            hasColor: false,
            code: '',
        };

        newVal.forEach(item => {
            let start = dateutil.IEDate(item.start.dateTime);
            let end = dateutil.IEDate(item.end.dateTime);

            let days: CalendarDayInfo[] = [];
            if (this.isOneDayEvent(item)) {
                days.push(this.findDay(start)!);
            } else {
                days = this.findDays(start, end);
            }

            days.forEach((day) => {
                if (!day.colors.includes(colorEvent)) day.colors.push(colorEvent);
            });
        });
    }

    //
    // ────────────────────────────────────────────────────────── I ──────────
    //   :::::: L I F E S T Y L E : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────────
    //

    created() {                  
        this.calcMatrix(false)
    }

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //

    // ────────────────────────────────────────────────────────────────────────────────
    // event handlers

    handleSelected(day: CalendarDayInfo) {
        this.$emit('input', day.dateValue)
    }

    // ────────────────────────────────────────────────────────────────────────────────
    // calc & render calendar

    /**
     * 달력에 출력할 month 기준 day 를 이차원 배열로 정리
     */
    calcMatrix(refreshEvent: boolean) {
        let datePickerMatrix = []
        let rows = []
        let today = new Date()
        let daysList = dateutil.uGetDaysInMonth(this.date, true, 0, true)

        this.matrixStart = daysList[0]
        this.matrixEnd = daysList[daysList.length - 1]

        for (let i = 0; i < daysList.length; i++) {
            let date = daysList[i]

            let isToday =
                date.getFullYear() == today.getFullYear() &&
                date.getMonth() == today.getMonth() &&
                date.getDate() == today.getDate()

            let sDate = dateutil.uGetDateFormat('yyyy-MM-DD', date)
            let cDate = dateutil.uGetDateFormat(
                'yyyy-MM-DD',
                dateutil.IEDate(this.value)
            )

            let dateItem: CalendarDayInfo = {
                date: date,
                dateValue: sDate,
                month: date.getMonth(),
                day: date.getDate(),
                colors: [],
                selected: sDate == cDate,
                isToday: isToday,
                isSun: date.getDay() == 0,
                isSat: date.getDay() == 6,
                isOut:
                    date.getMonth() != dateutil.IEDate(this.value).getMonth(),
            }

            rows.push(dateItem)

            if ((i + 1) % 7 == 0) {
                datePickerMatrix.push(rows)
                rows = []
            }
        }

        this.MonthMatrix = datePickerMatrix     
        
        if(refreshEvent) {
            this.getMonthlyEvents({ start: this.matrixStart, end: this.matrixEnd })
        } else {
            this.onEventsChanged(this.monthlyEvents);
        }
    }

    getEvents(
        folders: Array<CalendarFolderInfo>,
        abort: boolean = false
    ) {
        if (abort || this.preview || !folders || folders.length == 0) return                
        this.getMonthlyEvents({ start: this.matrixStart, end: this.matrixEnd });                    
    }

    findColor(folderId: string) {
        return this.folders.find((item) => {
            return item.id
        })?.color
    }

    findColorCode(folderId: string) {
        return this.folders.find((item) => {
            return item.id
        })?.color.code
    }

    // matrix에서 시작 ~ 종료 기간 사이에 있는 일수를 조회
    findDays(start: Date, end: Date): CalendarDayInfo[] {
        const dates = dateutil.getDaysArray(start, end).map((x) => dateutil.uGetDateFormat('yyyy-MM-DD', x));
        const days: CalendarDayInfo[] = []
        for (let week of this.MonthMatrix) {
            for (let day of week) {
                if (dates.includes(day.dateValue)) {
                    days.push(day)
                }
            }
        }
        return days
    }

    findDay(day: Date): CalendarDayInfo | null {
        const target = dateutil.uGetDateFormat('yyyy-MM-DD', day)
        let foundDay: CalendarDayInfo | null = null;

        for (let week of this.MonthMatrix) {
            for (let day of week) {
                if (day.dateValue === target) {
                    foundDay = day;
                }
            }
        }

        return foundDay;
    }

    isOneDayEvent(item: graph.CalendarItem) {
        let start = dateutil.IEDate(item.start.dateTime);
        let end = dateutil.IEDate(item.end.dateTime);
        
        let duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        end = new Date(end.getTime() - 1000);

        if (duration == 24 && start.toDateString() == end.toDateString()) {
            return true;
        } else {
            return false;
        }
    }

    changeMonth(count: number) {
        const copyDate = new Date(this.date)
        copyDate.setMonth(copyDate.getMonth() + count)
        this.changeValue(copyDate)
    }

    changeValue(date: Date) {
        this.$emit('input', dateutil.uGetDateFormat('yyyy-MM-DD', date))
    }
}